import React, { useEffect, useState, useRef } from 'react';

// API/Components
import {
	ANALYSIS_RESULT,
	validValue,
	AnalysisResultToStateColor,
	getMeasurementAnalysis,
} from '../enums';
import { DrawVerticalDashedLineAtTooltip } from '../chart_helpers';
import ResetZoomButton from '../reset_zoom_button';
import CustomGraphLegend from '../custom_graph_legend';

// Material UI
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';

// Chart.JS
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	Tooltip as ChartTooltip,
	BarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	ChartTooltip,
	BarController,
	zoomPlugin,
);


// Helper function to sort voltage first, then limits in proper order
function sortVoltageAndLimits(a, b, getLabel) {
	// Get labels using the provided getter function
	const aLabel = getLabel(a);
	const bLabel = getLabel(b);
	
	// Sort voltage first
	const aIsVoltage = aLabel?.includes('Voltage');
	const bIsVoltage = bLabel?.includes('Voltage');
	
	if (aIsVoltage && !bIsVoltage) return -1;
	if (!aIsVoltage && bIsVoltage) return 1;
	
	// Both are limits, sort by desired order: Crit Hi, Maint Hi, Maint Lo, Crit Lo
	const limitOrder = {
		'Crit High': 0,
		'Maint High': 1,
		'Maint Low': 2,
		'Crit Low': 3,
	};
	
	const aOrder = limitOrder[aLabel] ?? 999;
	const bOrder = limitOrder[bLabel] ?? 999;
	
	return aOrder - bOrder;
}


// Units Realtime Graph Component
export default function UnitsRealtimeGraph(props) {

	const { unitRealtimeData, limitData, graphScales, selectedString, isInDischarge, hasValidData, loading } = props;

	const [graphData, setGraphData] = useState({});
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
	});
	const [graphOptions, setGraphOptions] = useState({});

	const chartRef = useRef(null);
	const processedUnitsRef = useRef([]);
	const labelsRef = useRef([]);

	// Update chart options when graphScales changes
	useEffect(() => {
		const options = createChartOptions(graphScales);
		setGraphOptions(options);
	}, [graphScales, isInDischarge]);

	// Process unit realtime data and update graph datasets
	useEffect(() => {
		if (!unitRealtimeData || unitRealtimeData.length === 0 || !limitData || limitData.length === 0 || !hasValidData) { return; }

		// Filter units based on selectedString
		const filteredUnits = selectedString === 0 || selectedString === null || selectedString === undefined
			? unitRealtimeData
			: unitRealtimeData.filter(unit => unit.string_id === selectedString);

		// If no units match the filter, clear the chart
		if (filteredUnits.length === 0) {
			setGraphData({ labels: [], datasets: [] });
			return;
		}

		// Process the filtered unit data structure
		const processedData = processUnitData(filteredUnits, limitData);
		
		// Store processed units and labels for reference
		processedUnitsRef.current = processedData.units;
		labelsRef.current = processedData.labels;

		// If chart exists, update directly (preserves zoom/pan), otherwise initialize
		if (chartRef.current && chartRef.current.data) {
			updateExistingChart(processedData);
		} else {
			initializeChart(processedData);
		}

	}, [unitRealtimeData, limitData, graphSettings.limitsEnabled, selectedString, hasValidData]);

	// Process flat unit data and attach limits from limitData
	function processUnitData(units, limits) {

		// Pre-compute unit positions within each string
		const stringPositions = new Map();
		units.forEach(unit => {
			const stringKey = unit.string_id;
			if (!stringPositions.has(stringKey)) {
				stringPositions.set(stringKey, []);
			}
			stringPositions.get(stringKey).push(unit.unit_id);
		});

		// Sort unit IDs within each string to ensure correct position mapping
		stringPositions.forEach((unitIds, stringId) => {
			unitIds.sort((a, b) => a - b);
		});

		// Create a map of string limits for faster lookup
		const limitsMap = new Map();
		if (limits) {
			limits.forEach(stringLimit => {
				limitsMap.set(stringLimit.string_nbr, stringLimit);
			});
		}

		// Process each unit with efficient lookups
		const processedUnits = units.map(unit => {
			const stringLimits = limitsMap.get(unit.string_id);
			const unitPositionInString = stringPositions.get(unit.string_id)?.indexOf(unit.unit_id) ?? -1;
			
			return {
				...unit,
				limits: extractUnitLimits(stringLimits, unitPositionInString),
			};
		});

		// Create labels for each unit
		const labels = processedUnits.map(u => `S${u.string_id} U${u.unit_id}`);

		// Extract voltage values and compute analyses based on limits
		const voltages = processedUnits.map(u => validValue(u.voltage, null));
		const analyses = processedUnits.map(u => 
			getMeasurementAnalysis(u.voltage, u.limits?.hi_crit, u.limits?.lo_crit, u.limits?.hi_maint, u.limits?.lo_maint)
		);

		return {
			units: processedUnits,
			labels,
			voltages,
			analyses,
		};
	}

	// Extract limit values for a specific unit from string limits
	function extractUnitLimits(stringLimits, unitIndex) {
		if (!stringLimits || unitIndex < 0) {
			return { hi_crit: null, hi_maint: null, lo_maint: null, lo_crit: null };
		}

		return {
			hi_crit: stringLimits?.volt?.hi_crit?.[unitIndex],
			hi_maint: stringLimits?.volt?.hi_maint?.[unitIndex],
			lo_maint: stringLimits?.volt?.lo_maint?.[unitIndex],
			lo_crit: stringLimits?.volt?.lo_crit?.[unitIndex],
		};
	}

	// Update existing chart with new data (preserves zoom/pan)
	function updateExistingChart(processedData) {
		const { voltages, analyses, units, labels } = processedData;
		
		// Update voltage dataset in place
		const voltageDataset = chartRef.current.data.datasets[0];
		if (voltageDataset) {
			voltageDataset.data = voltages;
			voltageDataset.backgroundColor = createColorArray(analyses, 0.7);
			voltageDataset.borderColor = createColorArray(analyses, 1.0, true);
		}
		
		// Update limit datasets
		let datasets = [voltageDataset];
		datasets = addLimitDatasets(datasets, units, graphSettings.limitsEnabled);
		chartRef.current.data.datasets = datasets;
		
		// Update labels and chart without animation
		chartRef.current.data.labels = labels;
		chartRef.current.update('none');
	}

	// Initialize chart with data for first load
	function initializeChart(processedData) {
		const { voltages, analyses, units, labels } = processedData;
		
		let datasets = createVoltageDataset(voltages, analyses);
		datasets = addLimitDatasets(datasets, units, graphSettings.limitsEnabled);
		
		const data = {
			labels: labels,
			datasets: datasets,
		};
		setGraphData(data);
	}

	// Create color arrays based on analysis results
	function createColorArray(analyses, alpha, darken = false) {
		return analyses.map(analysis => {
			const analysisColor = AnalysisResultToStateColor(analysis.result);

			if (darken) {
				return tinycolor(analysisColor).darken(15).toHexString();
			}

			return tinycolor(analysisColor).setAlpha(alpha).toRgbString();
		});
	}

	// Create voltage bar dataset with analysis-based colors
	function createVoltageDataset(voltages, analyses) {
		const voltageDataset = {
			label: 'Voltage (V)',
			data: voltages,
			backgroundColor: createColorArray(analyses, 0.7),
			borderColor: createColorArray(analyses, 1.0, true),
			borderWidth: 2,
			yAxisID: 'y',
			barPercentage: 0.9,
			xAxisID: 'x',
			order: 1,
		};

		return [voltageDataset];
	};

	// Create a scatter plot limit marker dataset
	function createLimitScatter(label, limitValues, color) {
		return {
			label,
			data: limitValues,
			type: 'scatter',
			backgroundColor: color + "77",
			borderColor: color,
			borderWidth: 1,
			showLine: false,
			radius: 2,
			pointStyle: 'rect',
			spanGaps: true,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 0,
		};
	}

	// Add limit scatter datasets if enabled and limit data exists
	function addLimitDatasets(datasets, processedUnits, limitsEnabled) {
		if (!limitsEnabled || !processedUnits || processedUnits.length === 0) {
			return datasets;
		}

		// Get limit colors based on analysis results
		const limitColors = {
			hiCrit: AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL),
			hiMaint: AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS),
			loMaint: AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS),
			loCrit: AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL),
		};

		// Extract limit arrays from processed units
		const limitArrays = {
			hiCrit: processedUnits.map(u => u.limits?.hi_crit),
			hiMaint: processedUnits.map(u => u.limits?.hi_maint),
			loMaint: processedUnits.map(u => u.limits?.lo_maint),
			loCrit: processedUnits.map(u => u.limits?.lo_crit),
		};

		// Helper to check if limit array has valid values
		const hasValidLimits = (arr) => arr.some(v => v !== null && v !== undefined);

		// Add each limit type if it has valid data
		if (hasValidLimits(limitArrays.hiCrit)) {
			datasets.push(createLimitScatter('Crit High', limitArrays.hiCrit, limitColors.hiCrit));
		}
		if (hasValidLimits(limitArrays.hiMaint)) {
			datasets.push(createLimitScatter('Maint High', limitArrays.hiMaint, limitColors.hiMaint));
		}
		if (hasValidLimits(limitArrays.loMaint)) {
			datasets.push(createLimitScatter('Maint Low', limitArrays.loMaint, limitColors.loMaint));
		}
		if (hasValidLimits(limitArrays.loCrit)) {
			datasets.push(createLimitScatter('Crit Low', limitArrays.loCrit, limitColors.loCrit));
		}

		return datasets;
	};

	// Create chart options configuration
	function createChartOptions(graphScales) {
		const {
			voltDisplayMin,
			voltDisplayMax,
			voltBoundsMin,
			voltBoundsMax
		} = graphScales;

		return {
			maintainAspectRatio: false,
			animation: false,
			responsive: true,
			interaction: {
				mode: 'index',
				intersect: false,
			},
			scales: {
				x: {
					grid: {
						display: false,
					},
				},
				'y': {
					type: 'linear',
					position: 'left',
					min: voltDisplayMin,
					max: voltDisplayMax,
					title: {
						display: true,
						text: 'Voltage (V)',
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						color: 'rgba(0, 0, 0, 0.1)',
					},
				},
			},
			plugins: {
				legend: {
					display: false,
				},
				tooltip: {
					filter: function(tooltipItem) {
						// Only show tooltip items that have actual values (not null)
						return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
					},
					itemSort: (a, b) => sortVoltageAndLimits(a, b, (item) => item.dataset.label),
					callbacks: {
						beforeLabel: function(context) {
							// Check if this is the first limit item (after voltage)
							const currentIsLimit = !context.dataset.label?.includes('Voltage');
							
							if (currentIsLimit && context.datasetIndex > 0) {
								// Find the actual previous item in the sorted tooltip
								const tooltipItems = context.chart.tooltip.dataPoints;
								const currentIndex = tooltipItems.findIndex(item => 
									item.datasetIndex === context.datasetIndex
								);
								
								if (currentIndex > 0) {
									const prevItem = tooltipItems[currentIndex - 1];
									const prevItemIsVoltage = prevItem.dataset.label?.includes('Voltage');
									
									if (prevItemIsVoltage) {
										return '\n─── Limits ───';
									}
								}
							}
							return null;
						},
						label: function(context) {
							const datasetLabel = context.dataset.label || '';
							const displayValue = context.parsed.y;

							// Format voltage value
							return `${datasetLabel}: ${displayValue.toFixed(3)} V`;
						},
					},
				},
				zoom: {
					pan: {
						enabled: true,
						mode: 'xy',
					},
					limits: {
						'y': {
							min: voltBoundsMin,
							max: voltBoundsMax,
							minRange: 0.5,
						},
					},
					zoom: {
						mode: 'y',
						wheel: {
							enabled: true,
							speed: 0.05,
						},
						pinch: {
							enabled: true,
						},
						onZoomStart: (e) => {
							if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
								return;
							}
							if (e.event.shiftKey === true) {
								e.chart.config.options.plugins.zoom.zoom.mode = "x";
								e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
							} else {
								e.chart.config.options.plugins.zoom.zoom.mode = "y";
								e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
							}
						},
					},
				},
			},
		};
	};

	const handleLimitMarkersToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			limitsEnabled: !prev.limitsEnabled,
		}));
	};

	const handleGraphResetZoom = () => {
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingLeft: '5px',
				paddingRight: '5px',
				paddingBottom: '3px',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			<div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center', flexShrink: 0 }}>
				<strong>Unit Voltages {selectedString !== 0 ? `(String ${selectedString})` : ''}</strong>
			</div>
			<div style={{ 
				flex: '1 1 auto',
				width: '100%',
				minHeight: 0,
				overflow: 'hidden',
			}}>
				{ (hasValidData && graphData && graphData.datasets && graphData.datasets.length > 0) ? (
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
						plugins={[{ afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart) }]}
					/>
				) : (
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						color: '#666',
					}}>
						{loading ? 'Loading...' : 'No data available'}
					</div>
				)}
			</div>

			<Grid container spacing={1} alignItems="center">
				<Grid item>
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '2px' }}
					>
						<Tooltip title="Toggle limit markers on the graph" arrow>
							<ToggleButton
								value="limitMarkers"
								size="small"
								selected={graphSettings.limitsEnabled}
								onClick={handleLimitMarkersToggle}
								sx={{ 
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Limits
							</ToggleButton>
						</Tooltip>
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 0.5, columnGap: 2, padding: '4px 0' }}>
					<CustomGraphLegend
						datasets={graphData.datasets}
						sortFunction={sortVoltageAndLimits}
						customColorForArrays={AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS)}
					/>
				</Grid>
				<Grid item>
					<ResetZoomButton onClick={handleGraphResetZoom} />
				</Grid>
			</Grid>
		</div>
	);
}